import { Icon, Popover, useDisclosure } from '@chakra-ui/react';
import { FilterTypeOperator } from '@monetize/types/common';
import isObject from 'lodash/isObject';
import { forwardRef, useId, useImperativeHandle, useReducer } from 'react';
import FocusLock from 'react-focus-lock';
import { MdClose, MdFilterAlt, MdFilterList } from 'react-icons/md';
import { useNonInitialEffect } from '../../../../hooks/useNonInitialEffect';
import MButton from '../../MButton';
import MCustomIconButton from '../../MCustomIconButton';
import { MCustomSelect } from '../../MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../../MCustomSelect/components/MainSearchInputV2';
import MFormField from '../../MFormField';
import MInput from '../../MInput';
import { FOCUS_WITHOUT_SCROLLING } from '../../Overlays';
import { MFlex } from '../../chakra';
import {
  DataTableFilterRef,
  FilterProps,
  NumberFilterComparison,
  NumberFilterData,
  isNumberBetweenComparison,
  isNumberBlankNotBlankComparison,
} from '../DataTableTypes';
import { getColumnFilterKey } from '../tableUtilsNew';

// Stable value to avoid re-renders
const EMPTY_ARRAY: Array<{ label: string; value: string | number | boolean }> =
  [];

// Map FilterTypeOperator to NumberFilterComparison
const mapOperatorToComparison = (
  operator: string,
): NumberFilterComparison | null => {
  switch (operator) {
    case FilterTypeOperator.EQ:
      return NumberFilterComparison.EQUALS;
    case FilterTypeOperator.GT:
      return NumberFilterComparison.GREATER_THAN;
    case FilterTypeOperator.GTE:
      return NumberFilterComparison.GREATER_THAN_EQUAL;
    case FilterTypeOperator.LT:
      return NumberFilterComparison.LESS_THAN;
    case FilterTypeOperator.LTE:
      return NumberFilterComparison.LESS_THAN_EQUAL;
    case FilterTypeOperator.BETWEEN:
      return NumberFilterComparison.BETWEEN_INCLUSIVE;
    default:
      return null;
  }
};

// Base comparison values for server-side filtering
const SERVER_COMPARISON_VALUES = [
  { title: 'Equals', value: NumberFilterComparison.EQUALS },
  { title: 'Greater Than', value: NumberFilterComparison.GREATER_THAN },
  {
    title: 'Greater Than or Equal',
    value: NumberFilterComparison.GREATER_THAN_EQUAL,
  },
  { title: 'Less Than', value: NumberFilterComparison.LESS_THAN },
  {
    title: 'Less Than or Equal',
    value: NumberFilterComparison.LESS_THAN_EQUAL,
  },
  {
    title: 'Between (Inclusive)',
    value: NumberFilterComparison.BETWEEN_INCLUSIVE,
  },
];

// Additional comparison values for client-side filtering only
const CLIENT_ONLY_COMPARISON_VALUES = [
  { title: 'Is Blank', value: NumberFilterComparison.IS_BLANK },
  { title: 'Is Not Blank', value: NumberFilterComparison.IS_NOT_BLANK },
];

// Helper function to get appropriate comparison values based on clientControlled
const getComparisonValues = (clientControlled?: boolean) => {
  if (clientControlled) {
    return [...CLIENT_ONLY_COMPARISON_VALUES, ...SERVER_COMPARISON_VALUES];
  }
  return SERVER_COMPARISON_VALUES;
};

interface NumberFilterState {
  comparison: NumberFilterComparison;
  firstValue: string;
  secondValue: string;
}

type NumberFilterAction =
  | { type: 'SET_COMPARISON'; payload: NumberFilterComparison }
  | { type: 'SET_FIRST_VALUE'; payload: string }
  | { type: 'SET_SECOND_VALUE'; payload: string }
  | { type: 'RESET' }
  | { type: 'SET_ALL'; payload: NumberFilterState };

const initialFilterState: NumberFilterState = {
  comparison: NumberFilterComparison.GREATER_THAN,
  firstValue: '',
  secondValue: '',
};

const numberFilterReducer = (
  state: NumberFilterState,
  action: NumberFilterAction,
): NumberFilterState => {
  switch (action.type) {
    case 'SET_COMPARISON':
      return { ...state, comparison: action.payload };
    case 'SET_FIRST_VALUE':
      return { ...state, firstValue: action.payload };
    case 'SET_SECOND_VALUE':
      return { ...state, secondValue: action.payload };
    case 'RESET':
      return initialFilterState;
    case 'SET_ALL':
      return action.payload;
    default:
      return state;
  }
};

export const DataTableHeaderNumberFilter = forwardRef<
  DataTableFilterRef,
  FilterProps<any>
>(({ column, table, clientControlled }, ref) => {
  useImperativeHandle(ref, (): DataTableFilterRef => ({
    resetFilters: () => {
      handleResetFilters();
    },
    setFilterValue: ({ columnKey, value }) => {
      if (
        columnKey === column.id &&
        isObject(value) &&
        'comparison' in value &&
        'firstValue' in value
      ) {
        column.setFilterValue(value);
      }
    },
  }));

  const key = getColumnFilterKey(column);
  const filterValues =
    table.options.meta?.filterValues?.get(key) || EMPTY_ARRAY;

  const { open, onOpen, onClose } = useDisclosure();

  // Get initial state from meta.selectedFilters if it exists
  const getInitialState = (): NumberFilterState => {
    const existingFilter = table.options.meta?.selectedFilters?.get(key);
    if (
      existingFilter &&
      typeof existingFilter === 'object' &&
      'operator' in existingFilter
    ) {
      const mappedComparison = mapOperatorToComparison(
        existingFilter.operator as string,
      );
      return {
        comparison: mappedComparison ?? initialFilterState.comparison,
        firstValue: existingFilter?.['min'] ?? '',
        secondValue: existingFilter?.['max'] ?? '',
      } as NumberFilterState;
    }
    return initialFilterState;
  };

  // Store applied state separately from working state
  const [appliedState, setAppliedState] = useReducer(
    numberFilterReducer,
    getInitialState(),
  );

  const [selectedState, dispatchSelected] = useReducer(
    numberFilterReducer,
    getInitialState(),
  );

  useNonInitialEffect(() => {
    // Only update column filter when popover is closed (filters are applied)
    if (!open) {
      const hasValue =
        isNumberBlankNotBlankComparison(selectedState.comparison) ||
        !!selectedState.firstValue;
      if (hasValue) {
        const numberFilterData: NumberFilterData = {
          comparison: selectedState.comparison,
          firstValue: selectedState.firstValue,
          secondValue: selectedState.secondValue,
        };
        column.setFilterValue(numberFilterData);
      } else {
        column.setFilterValue(undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedState.comparison,
    selectedState.firstValue,
    selectedState.secondValue,
    open,
  ]);

  const handleResetFilters = () => {
    dispatchSelected({ type: 'RESET' });
    setAppliedState({ type: 'RESET' });
    column.setFilterValue(undefined);

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, undefined);
    }

    // Close the popover directly
    onClose();
  };

  const handleApplyFilters = () => {
    // Update applied state to match selected state
    setAppliedState({ type: 'SET_ALL', payload: selectedState });

    const numberFilterData: NumberFilterData = {
      comparison: selectedState.comparison,
      firstValue: selectedState.firstValue,
      secondValue: selectedState.secondValue,
    };

    if (table.options.meta?.onFilterUpdate) {
      table.options.meta.onFilterUpdate(key, numberFilterData);
    }
    onClose();
  };

  const handleClose = () => {
    // Revert to last applied state when closing without applying
    dispatchSelected({ type: 'SET_ALL', payload: appliedState });
    onClose();
  };

  const filtersApplied =
    isNumberBlankNotBlankComparison(appliedState.comparison) ||
    (!!filterValues &&
      appliedState.firstValue &&
      (!isNumberBetweenComparison(appliedState.comparison) ||
        !!appliedState.secondValue));

  // Check if current state differs from initial state
  const hasChanges =
    selectedState.comparison !== initialFilterState.comparison ||
    selectedState.firstValue !== initialFilterState.firstValue ||
    selectedState.secondValue !== initialFilterState.secondValue;

  const titleId = useId();

  return (
    <Popover.Root
      open={open}
      lazyMount
      unmountOnExit
      onOpenChange={(e) => {
        if (e.open) {
        } else {
          handleClose();
        }
      }}
      positioning={{
        placement: 'bottom-start',
        offset: { mainAxis: 0, crossAxis: 0 },
        strategy: 'fixed',
      }}
    >
      <Popover.Trigger asChild>
        <MCustomIconButton
          data-testid={`table-column-filter-${key}`}
          btnSize={5}
          onClick={onOpen}
          icon={filtersApplied ? MdFilterAlt : MdFilterList}
          aria-label="Filter"
          variant="icon"
          ml="2"
          color={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
          iconColor={filtersApplied ? 'tIndigo.base' : 'tPurple.base'}
        />
      </Popover.Trigger>
      <Popover.Positioner>
        <Popover.Content aria-labelledby={titleId}>
          {open && (
            <FocusLock focusOptions={FOCUS_WITHOUT_SCROLLING}>
              <Popover.Arrow>
                <Popover.ArrowTip />
              </Popover.Arrow>
              <Popover.CloseTrigger aria-label="Close">
                <Icon asChild>
                  <MdClose />
                </Icon>
              </Popover.CloseTrigger>
              <Popover.Header id={titleId}>Filter</Popover.Header>
              <Popover.Body minW="304px">
                <MFormField label="Comparison">
                  <MCustomSelect
                    useMainInputAsSearchInput
                    MainInputComponent={MainSearchInputV2}
                    items={getComparisonValues(clientControlled)}
                    itemTitle="title"
                    itemValue="value"
                    value={selectedState.comparison}
                    onBlur={(ev) => ev.preventDefault()}
                    onChange={(value: any) =>
                      dispatchSelected({
                        type: 'SET_COMPARISON',
                        payload: value as NumberFilterComparison,
                      })
                    }
                  />
                </MFormField>
                {!isNumberBlankNotBlankComparison(selectedState.comparison) && (
                  <>
                    <MFormField
                      pt={2}
                      label={
                        isNumberBetweenComparison(selectedState.comparison)
                          ? 'Min'
                          : undefined
                      }
                    >
                      <MInput
                        type="number"
                        value={selectedState.firstValue}
                        onChange={(e) => {
                          dispatchSelected({
                            type: 'SET_FIRST_VALUE',
                            payload: e.target.value,
                          });
                        }}
                        placeholder="Enter a number"
                      />
                    </MFormField>
                    {isNumberBetweenComparison(selectedState.comparison) && (
                      <MFormField pt={2} label="Max">
                        <MInput
                          type="number"
                          value={selectedState.secondValue}
                          onChange={(e) => {
                            dispatchSelected({
                              type: 'SET_SECOND_VALUE',
                              payload: e.target.value,
                            });
                          }}
                          placeholder="Enter a number"
                        />
                      </MFormField>
                    )}
                  </>
                )}
              </Popover.Body>
              <Popover.Footer>
                <MFlex justifyContent="space-between">
                  <MButton
                    variant="tertiary"
                    size="sm"
                    onClick={handleResetFilters}
                    isDisabled={!hasChanges}
                  >
                    Reset
                  </MButton>
                  <MButton size="sm" onClick={handleApplyFilters}>
                    Apply
                  </MButton>
                </MFlex>
              </Popover.Footer>
            </FocusLock>
          )}
        </Popover.Content>
      </Popover.Positioner>
    </Popover.Root>
  );
});
